export const DEFAULT_SCENE_TRANSITION_DURATION = 0.6;
export const POPUP_TRANSITION_DURATION = 0.4;
export const ANIMATION_CLEAR_PROPS = "opacity,visibility,transform,zIndex";
export const SCROLL_EFFECT_CLEAR_PROPS = `${ANIMATION_CLEAR_PROPS},filter,clipPath,--scene-curtain-clip,--scene-curtain-color`;

export const SCROLL_TRIGGER_CONFIG = {
    scrub: true,
    invalidateOnRefresh: true,
};

export const SCROLL_EFFECT_MODE_CONFIG = {
    in: {
        start: "top bottom",
        end: "top 50%",
    },
    out: {
        start: "bottom 50%",
        end: "bottom top",
    },
};

export const DEFAULT_SCENE_TRANSITION_SETTINGS = { type: "none" };

export const parseSceneTransitionSettings = (value) => {
    if (!value) {
        return DEFAULT_SCENE_TRANSITION_SETTINGS;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        return normalizeSceneTransitionSettings(value);
    }

    try {
        return normalizeSceneTransitionSettings(JSON.parse(value));
    } catch (error) {
        console.warn("[sceneMotion] Invalid scene transition settings:", value, error);
        return DEFAULT_SCENE_TRANSITION_SETTINGS;
    }
};

const normalizeSceneTransitionSettings = (settings) => {
    if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
        console.warn("[sceneMotion] Scene transition settings must be an object:", settings);
        return DEFAULT_SCENE_TRANSITION_SETTINGS;
    }

    return {
        ...settings,
        type: settings.type || DEFAULT_SCENE_TRANSITION_SETTINGS.type,
    };
};

const getSetting = (settings, key, fallback) => settings[key] ?? fallback;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getNumericSetting = (settings, key, fallback, min, max) => {
    const raw = getSetting(settings, key, fallback);
    // Number('') and Number('  ') are 0, which would silently clamp an empty authored value to
    // `min` instead of using the preset default; treat blank strings as missing, like null/undefined.
    if (typeof raw === "string" && raw.trim() === "") {
        return fallback;
    }
    const value = Number(raw);
    return Number.isFinite(value) ? clamp(value, min, max) : fallback;
};

const getPopupDuration = (settings) => (
    getNumericSetting(settings, "duration", POPUP_TRANSITION_DURATION, 0.1, 2)
);

const getPopupMovement = (settings, distance, fallbackDirection) => {
    const direction = getSetting(settings, "direction", fallbackDirection);
    const movements = {
        tm: { yPercent: -distance },
        mr: { xPercent: distance },
        bm: { yPercent: distance },
        ml: { xPercent: -distance },
    };
    return movements[direction] || movements[fallbackDirection];
};

const createCurtainRevealEffect = (settings) => ({
    mode: getSetting(settings, "mode", "in"),
    neutralState: {
        "--scene-curtain-clip": "100%",
        "--scene-curtain-color": getSetting(settings, "color", "var(--scene-curtain-default-color, #fff)"),
    },
    effectState: {
        "--scene-curtain-clip": "0%",
        "--scene-curtain-color": getSetting(settings, "color", "var(--scene-curtain-default-color, #fff)"),
        duration: 1,
    },
});

export const SCROLLING_SCENE_EFFECTS = {
    none: () => null,
    slowParallax: (settings) => ({
        mode: getSetting(settings, "mode", "out"),
        neutralState: {
            yPercent: 0,
            opacity: 1,
        },
        effectState: {
            yPercent: 36 * getSetting(settings, "speed", 0.5),
            opacity: 1 - getSetting(settings, "dim", 0),
            duration: 1,
            force3D: true,
        },
        // markers: true,
    }),
    zoom: (settings) => ({
        mode: getSetting(settings, "mode", "in"),
        neutralState: { scale: 1 },
        effectState: {
            scale: getSetting(settings, "scale", 1.25),
            transformOrigin: "50% 50%",
            duration: 1,
            force3D: true,
        },
        // markers: true,
    }),
    blur: (settings) => ({
        mode: getSetting(settings, "mode", "out"),
        neutralState: { filter: "blur(0px)" },
        effectState: {
            filter: `blur(${getSetting(settings, "blur", 10)}px)`,
            duration: 1,
        },
        // markers: true,
    }),
    recede: (settings) => ({
        mode: getSetting(settings, "mode", "out"),
        neutralState: {
            scale: 1,
            opacity: 1,
        },
        effectState: {
            scale: getSetting(settings, "scale", 0.9),
            opacity: 1 - getSetting(settings, "dim", 0.4),
            transformOrigin: "50% 50%",
            duration: 1,
            force3D: true,
        },
        // markers: true,
    }),
    // curtain: (settings) => ({
    //     mode: getSetting(settings, "mode", "in"),
    //     neutralState: {
    //         "--scene-curtain-clip": "100%",
    //         "--scene-curtain-color": getSetting(settings, "color", "var(--scene-curtain-default-color, #fff)"),
    //     },
    //     effectState: {
    //         "--scene-curtain-clip": "0%",
    //         "--scene-curtain-color": getSetting(settings, "color", "var(--scene-curtain-default-color, #fff)"),
    //         duration: 1,
    //     },
    // }),
    split: (settings) => ({
        mode: getSetting(settings, "mode", "in"),
        neutralState: { clipPath: "inset(0%)" },
        effectState: {
            clipPath: getSetting(settings, "axis", "vertical") ? "inset(50% 0 50% 0)" : "inset(0 50% 0 50%)",
            duration: 1,
        },
    }),
    iris: (settings) => ({
        mode: getSetting(settings, "mode", "in"),
        neutralState: { clipPath: getSetting(settings, "origin", "center") ? "circle(150% at 50% 50%)" : "circle(150% at 0% 100%)" },
        effectState: {
            clipPath: getSetting(settings, "origin", "center") ? "circle(0% at 50% 50%)" : "circle(0% at 0% 100%)",
            duration: 1,
        },
    }),
    diagonal: (settings) => {
        const slant = getSetting(settings, "slant", 20)

        return {
            mode: getSetting(settings, "mode", "in"),
            neutralState: {
                clipPath: `polygon(0% 0%, ${100 + slant}% 0%, 100% 100%, 0% 100%)`,
            },
            effectState: {
                clipPath: `polygon(0% 0%, ${-slant}% 0%, ${-2 * slant}% 100%, 0% 100%)`,
                duration: 1,
            },
        };
    },
};

export const SCENE_TRANSITIONS = {
    none: () => ({
        duration: 0,
    }),
    crossFade: () => ({
        duration: 0.7,
        oldTo: { autoAlpha: 0 },
        newFrom: { autoAlpha: 0 },
        newTo: { autoAlpha: 1 },
        ease: "none",
    }),
    fadeMove: (direction) => ({
        duration: 0.7,
        oldTo: { autoAlpha: 0 },
        newFrom: { autoAlpha: 0, yPercent: 100 * direction },
        newTo: { autoAlpha: 1, yPercent: 0 },
        ease: "power2.out",
    }),
    // legacy pt-page-moveToTop/ontop + pt-page-scaleUp: the old scene slides away
    // on top while the new one scales up from behind it
    moveScale: (direction) => ({
        duration: 0.7,
        oldFrom: { zIndex: 3 },
        oldTo: { yPercent: -100 * direction },
        newFrom: { autoAlpha: 0, scale: 0.8 },
        newTo: { autoAlpha: 1, scale: 1 },
        ease: "power2.out",
    }),
    // legacy pt-page-moveToTopEasing/ontop + pt-page-moveFromBottom: a scroll over
    // the scene's own height, with the old scene layered on top
    differentEasing: (direction) => ({
        duration: 0.7,
        oldFrom: { zIndex: 3 },
        oldTo: { yPercent: -100 * direction },
        newFrom: { yPercent: 100 * direction },
        newTo: { yPercent: 0 },
        ease: "power1.inOut",
    }),
    scroll: (direction) => ({
        duration: DEFAULT_SCENE_TRANSITION_DURATION,
        oldTo: { y: `${-100 * direction}vh` },
        newFrom: { y: `${100 * direction}vh` },
        newTo: { y: 0 },
        ease: "power2.inOut",
    }),
};

export const POPUP_TRANSITIONS = {
    none: () => ({
        duration: 0,
        sceneFrom: {},
        sceneTo: {},
        sceneOut: {},
        contentFrom: {},
        contentTo: {},
        contentOut: {},
    }),
    fade: (settings = {}) => ({
        duration: getPopupDuration(settings),
        sceneFrom: { autoAlpha: 0 },
        sceneTo: { autoAlpha: 1 },
        sceneOut: { autoAlpha: 0 },
        contentFrom: { autoAlpha: 0 },
        contentTo: { autoAlpha: 1 },
        contentOut: { autoAlpha: 0 },
        easeIn: "power2.out",
        easeOut: "power2.in",
    }),
    fadeStep: (settings = {}) => ({
        duration: getPopupDuration(settings),
        sceneFrom: { autoAlpha: 0 },
        sceneTo: { autoAlpha: 1 },
        sceneOut: { autoAlpha: 0 },
        contentFrom: { autoAlpha: 0, ...getPopupMovement(settings, 10, "bm") },
        contentTo: { autoAlpha: 1, xPercent: 0, yPercent: 0 },
        contentOut: { autoAlpha: 0, ...getPopupMovement(settings, 10, "bm") },
        easeIn: "power2.out",
        easeOut: "power2.in",
    }),
    move: (settings = {}) => ({
        duration: getPopupDuration(settings),
        sceneFrom: { autoAlpha: 0 },
        sceneTo: { autoAlpha: 1 },
        sceneOut: { autoAlpha: 0 },
        contentFrom: { autoAlpha: 0, ...getPopupMovement(settings, 100, "mr") },
        contentTo: { autoAlpha: 1, xPercent: 0, yPercent: 0 },
        contentOut: { autoAlpha: 0, ...getPopupMovement(settings, 100, "mr") },
        easeIn: "power2.out",
        easeOut: "power2.in",
    }),
    zoom: (settings = {}) => ({
        duration: getPopupDuration(settings),
        sceneFrom: { autoAlpha: 0 },
        sceneTo: { autoAlpha: 1 },
        sceneOut: { autoAlpha: 0 },
        contentFrom: { autoAlpha: 0, scale: getNumericSetting(settings, "scale", 2, 1, 2) },
        contentTo: { autoAlpha: 1, scale: 1 },
        contentOut: { autoAlpha: 0, scale: getNumericSetting(settings, "scale", 2, 1, 2) },
        easeIn: "power2.out",
        easeOut: "power2.in",
    }),
};
